/**
 * useTablePersistence
 *
 * URL + backend-preference persistence for the new `@onengine/component-library`
 * Table. It is the Option-B replacement for `useTableUrlPersistence`: it reuses
 * that hook's page / sort / search / URL-sync / view-preference mechanics and the
 * same `filterBy` JSON schema (so URLs and saved preferences stay cross-compatible
 * with the old tables), but drops the Zustand filter store entirely.
 *
 * The new Table owns its own filter draft/committed state and surfaces committed
 * selections as `Record<columnId, string[]>` (display labels) via `onFilter`, so
 * there is nothing to stage in Zustand. Label↔backend-id translation lives in the
 * filter shim (`useTableFilterOptions`); this hook delegates serialization to it
 * via `toFilterEntries` and re-seeds it on async preference restore via
 * `seedAppliedOptions`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { DEBOUNCED_WAIT_TIME } from "@/components/data/constants";
import { useGetViewPreferences, useSaveViewPreferences } from "@/hooks/useViewPreferences";
import { DEFAULT_TABLE_PAGE_SIZE } from "@/constants/tablePagination";
import type { TableUrlSearchParams } from "@/hooks/useTableUrlPersistence";
import type { FilterOption, TableFilterColumnConfig } from "@/hooks/table/useTableFilterOptions";
import type { FilterEntry } from "@/utils/buildFilterByParam";

export type TableSortState = { columnId: string; direction: "asc" | "desc" } | null;

type StoredFilterEntry = { id: string; value: string; label?: string } | string;

/**
 * Parses a `filterBy` JSON string (`{ filterByKey: [{id,value,label}] }`, the
 * shared schema) into the two shapes the new Table + shim need:
 *  - `filters`: committed display labels per columnId (for `<Table filters>`)
 *  - `appliedOptions`: label→id options per columnId (to seed the shim's map so
 *    restored selections resolve to backend ids without reopening the chip)
 *
 * Pure and standalone so it can seed the shim's `initialAppliedOptions` at
 * construction and be unit-tested in isolation.
 */
export function parseFilterByToColumns(
  filterByJson: string | undefined,
  columns: TableFilterColumnConfig[],
): { filters: Record<string, string[]>; appliedOptions: Record<string, FilterOption[]> } {
  const filters: Record<string, string[]> = {};
  const appliedOptions: Record<string, FilterOption[]> = {};
  if (!filterByJson) return { filters, appliedOptions };

  const columnIdByFilterKey = new Map<string, string>();
  columns.forEach((col) => columnIdByFilterKey.set(col.filterByKey, col.columnId));

  try {
    const parsed = JSON.parse(filterByJson) as Record<string, StoredFilterEntry[]>;
    Object.entries(parsed).forEach(([filterByKey, entries]) => {
      const columnId = columnIdByFilterKey.get(filterByKey);
      if (!columnId || !Array.isArray(entries)) return;
      entries.forEach((entry) => {
        const option: FilterOption =
          typeof entry === "string"
            ? { id: entry, label: entry }
            : { id: entry.id, label: entry.label ?? entry.value };
        filters[columnId] = [...(filters[columnId] ?? []), option.label];
        appliedOptions[columnId] = [...(appliedOptions[columnId] ?? []), option];
      });
    });
  } catch {
    return { filters: {}, appliedOptions: {} };
  }

  return { filters, appliedOptions };
}

/** Groups the shim's flat FilterEntry[] into the shared `filterBy` JSON shape. */
function filterEntriesToFilterBy(entries: FilterEntry[]): string | undefined {
  if (!entries.length) return undefined;
  const obj: Record<string, Array<{ id: string; value: string; label?: string }>> = {};
  entries.forEach(({ filterBy, filterValue }) => {
    obj[filterBy] = [
      ...(obj[filterBy] ?? []),
      { id: filterValue.id, value: filterValue.value, label: filterValue.label },
    ];
  });
  return Object.keys(obj).length ? JSON.stringify(obj) : undefined;
}

export type UseTablePersistenceOptions = {
  viewName: string;
  columns: TableFilterColumnConfig[];
  searchParams: TableUrlSearchParams;
  navigate: (opts: any) => void;
  enableUrlSync?: boolean;
  /** From the shim — resolves committed labels → FilterEntry[] for serialization. */
  toFilterEntries: (filters: Record<string, string[]>) => FilterEntry[];
  /** From the shim — re-seeds its label→id map when filters restore from prefs. */
  seedAppliedOptions: (options: Record<string, FilterOption[]>) => void;
  restoreSavedPage?: boolean;
  defaultPageSize?: number;
};

export type UseTablePersistenceReturn = {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: (value: string) => void;
  debouncedSearch: string;
  sorting: TableSortState;
  onSort: (columnId: string | null, direction: "asc" | "desc") => void;
  groupBy: TableSortState;
  onGroup: (columnId: string | null, direction: "asc" | "desc") => void;
  filters: Record<string, string[]>;
  onFilter: (filters: Record<string, string[]>) => void;
  filtersKey: string;
  isPrefsLoading: boolean;
  isPrefsReady: boolean;
};

export function useTablePersistence({
  viewName,
  columns,
  searchParams,
  navigate,
  enableUrlSync = true,
  toFilterEntries,
  seedAppliedOptions,
  restoreSavedPage = true,
  defaultPageSize = DEFAULT_TABLE_PAGE_SIZE,
}: UseTablePersistenceOptions): UseTablePersistenceReturn {
  const [page, setPage] = useState(() => (enableUrlSync ? (searchParams.page ?? 1) : 1));
  const [pageSize, setPageSize] = useState(() =>
    enableUrlSync ? (searchParams.limit ?? defaultPageSize) : defaultPageSize,
  );
  const [search, setSearch] = useState(() => (enableUrlSync ? (searchParams.search ?? "") : ""));
  const [debouncedSearch] = useDebouncedValue(search, DEBOUNCED_WAIT_TIME);

  const [sorting, setSorting] = useState<TableSortState>(() => {
    if (enableUrlSync && searchParams.sortBy) {
      return { columnId: searchParams.sortBy, direction: searchParams.sortOrder ?? "asc" };
    }
    return null;
  });

  const [groupBy, setGroupBy] = useState<TableSortState>(null);

  const [filters, setFilters] = useState<Record<string, string[]>>(() =>
    enableUrlSync ? parseFilterByToColumns(searchParams.filterBy, columns).filters : {},
  );
  const filtersKey = JSON.stringify(filters);

  const { mutate: savePreferences } = useSaveViewPreferences(viewName);

  const onSort = useCallback((columnId: string | null, direction: "asc" | "desc") => {
    setSorting(columnId ? { columnId, direction } : null);
  }, []);

  const onGroup = useCallback((columnId: string | null, direction: "asc" | "desc") => {
    setGroupBy(columnId ? { columnId, direction } : null);
  }, []);

  const onFilter = useCallback((next: Record<string, string[]>) => {
    const cleaned: Record<string, string[]> = {};
    Object.entries(next).forEach(([key, values]) => {
      if (values?.length) cleaned[key] = values;
    });
    setFilters(cleaned);
    setPage(1);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const urlSyncParams = useMemo(() => {
    const filterBy = filterEntriesToFilterBy(toFilterEntries(filters));
    return {
      page,
      limit: pageSize,
      search: debouncedSearch || undefined,
      sortBy: sorting?.columnId,
      sortOrder: sorting?.direction,
      filterBy,
    };
    // filtersKey is the stable serialization of `filters`
  }, [page, pageSize, debouncedSearch, sorting, filtersKey, toFilterEntries]);

  const urlSyncKey = JSON.stringify(urlSyncParams);

  useEffect(() => {
    if (!enableUrlSync) return;
    navigate({ search: (prev: any) => ({ ...prev, ...urlSyncParams }), replace: true });
  }, [urlSyncKey, enableUrlSync]);

  const {
    data: viewPreferences,
    isSuccess: prefsLoaded,
    isLoading: isGetPrefsLoading,
    isError: isPrefsError,
    isFetching: isPreferencesFetching,
  } = useGetViewPreferences({ viewName, enabled: true });

  const isPrefsReady = (prefsLoaded && !isPreferencesFetching) || isPrefsError;

  const hasRestoredFromPrefsRef = useRef(false);
  useEffect(() => {
    if (!prefsLoaded || isPreferencesFetching || !viewPreferences?.queryString) return;
    if (hasRestoredFromPrefsRef.current) return;
    hasRestoredFromPrefsRef.current = true;

    const params = new URLSearchParams(viewPreferences.queryString);
    const savedPage = Number.parseInt(params.get("page") ?? "1", 10);
    const savedLimit = Number.parseInt(params.get("limit") ?? String(defaultPageSize), 10);
    const savedSearch = params.get("search") ?? "";
    const savedSortBy = params.get("sortBy") ?? "";
    const savedSortOrder = params.get("sortOrder") as "asc" | "desc" | null;
    const savedFilterBy = params.get("filterBy") ?? undefined;

    if (restoreSavedPage && !Number.isNaN(savedPage)) setPage(savedPage);
    if (!Number.isNaN(savedLimit)) setPageSize(savedLimit);
    if (savedSearch) setSearch(savedSearch);
    if (savedSortBy) setSorting({ columnId: savedSortBy, direction: savedSortOrder ?? "asc" });

    const { filters: restoredFilters, appliedOptions } = parseFilterByToColumns(
      savedFilterBy,
      columns,
    );
    if (Object.keys(restoredFilters).length) {
      seedAppliedOptions(appliedOptions);
      setFilters(restoredFilters);
    }
  }, [prefsLoaded, isPreferencesFetching, viewPreferences]);

  const [debouncedSaveKey] = useDebouncedValue(
    JSON.stringify({ ...urlSyncParams, search: undefined }),
    1500,
  );

  const isInitializing = useRef(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitializing.current = false;
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isInitializing.current) return;
    const queryString = new URLSearchParams(
      Object.entries(urlSyncParams)
        .filter(([k, v]) => k !== "search" && v != null)
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    savePreferences({ queryString });
  }, [debouncedSaveKey]);

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    debouncedSearch,
    sorting,
    onSort,
    groupBy,
    onGroup,
    filters,
    onFilter,
    filtersKey,
    isPrefsLoading: isGetPrefsLoading,
    isPrefsReady,
  };
}
